import { useTranslate } from 'react-polyglot';

import type { SessionAnalyticsKpis } from '../../../../utils/buildSessionAnalytics';
import { Card, ExpandedValue, Grid, Label, Value } from './SessionKpis.styled';

/** Shown on every card until the host resolves the first question. */
const NO_VALUE = '–';

interface SessionKpisProps {
  kpis: SessionAnalyticsKpis | undefined;
  /**
   * Full screen stacks all six cards in a column. The side panel keeps its approved grid
   * of four: two more cards there would push the question list out of view, which is the
   * one thing that panel exists to show.
   */
  isExpanded?: boolean;
}

export const SessionKpis = ({ kpis, isExpanded = false }: SessionKpisProps) => {
  const t = useTranslate();

  // Keys are written out in full on purpose: building them by interpolating the KPI name
  // would hide them from the translation tooling.
  const cards = [
    {
      labelKey: '__new.play.analyticsResults.kpi.resolvedQuestions',
      value: kpis && String(kpis.resolvedQuestions),
    },
    {
      labelKey: '__new.play.analyticsResults.kpi.totalParticipants',
      value: kpis && String(kpis.totalParticipants),
    },
    {
      labelKey: '__new.play.analyticsResults.kpi.averageParticipation',
      value: kpis && `${kpis.averageParticipation}%`,
    },
    {
      labelKey: '__new.play.analyticsResults.kpi.hitRate',
      value: kpis && `${kpis.hitRate}%`,
    },
    {
      labelKey: '__new.play.analyticsResults.kpi.errorRate',
      value: kpis && `${kpis.errorRate}%`,
      isExpandedOnly: true,
    },
    {
      labelKey: '__new.play.analyticsResults.kpi.totalAnswers',
      value: kpis && String(kpis.totalAnswers),
      isExpandedOnly: true,
    },
  ];

  const ValueText = isExpanded ? ExpandedValue : Value;

  const shownCards = cards.filter(
    ({ isExpandedOnly }) => isExpanded || isExpandedOnly !== true,
  );

  return (
    <Grid $isExpanded={isExpanded}>
      {shownCards.map(({ labelKey, value }) => (
        <Card key={labelKey}>
          <ValueText>{value ?? NO_VALUE}</ValueText>
          <Label>{t(labelKey)}</Label>
        </Card>
      ))}
    </Grid>
  );
};
